// Used to visualize and edit a tournament schedule

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import Field from './Field';
import TimelineFields from './TimelineFields';
import ColorLegend from './ColorLegend';
import InputDate from './InputDate';
import Brackets from './Brackets';
import GetClickOutside from './GetClickOutside';
import FieldsList from './FieldsList';
import GameBlockEdit from './GameBlockEdit';
import ScheduleGenerator from './ScheduleGenerator';
import Select from './Select';
import SearchInput from './SearchInput';
import GuidedTour from './GuidedTour';
import HorizontalScrollbar from './HorizontalScrollbar';
import FieldHeadersRow from './FieldHeadersRow';
import { useModal } from './ModalProvider';
import { useFirebase } from './FirebaseContextProvider';
import { time24To12H, captureJpegBlob } from '../utils/misc';
import { gameDivision } from '../utils/gameReport';
import {
  tournamentGameUpdate,
  tournamentGamesDelete,
  tournamentUpdate,
} from '../utils/firebase';
import { storageField, storageDate } from '../utils/consts';
import { timeSlots } from '../utils/timeSlots';
import CheckBox from './CheckBox';
import { toast } from 'react-toastify';
import SchedulePrintView from './SchedulePrintView';
import './Scheduler.scss';

const steps = [
  {
    target: '.timeline-fields-wrap',
    title: 'Game schedule',
    content: "Here's the game schedule. Click on any game to enter the score.",
  },
];

export default function Scheduler({ id, colorLegend }) {
  const [spinner, setSpinner] = useState(false);
  const [bracketsPopup, setBracketsPopup] = useState(null);
  const [filteredGames, setFilteredGames] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(null);
  const [fieldFilter, setFieldFilter] = useState(() => {
    const saved = localStorage.getItem(storageField);
    if (!saved || saved === 'null' || saved === 'undefined') return null;
    return saved;
  });
  const [date, setDate] = useState(null);
  const [published, setPublished] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInputRequest, setSearchInputRequest] = useState(null);
  const [activeSearchHoverKey, setActiveSearchHoverKey] = useState(null);
  const scheduleWrapRef = useRef(null);
  const printRef = useRef(null);
  const {
    tournamentsById,
    teamsById,
    refereesById,
    userIsAdmin,
    userIsFieldManager,
    coachesByTeamId,
  } = useFirebase();
  const { openModal } = useModal();

  const tournament = tournamentsById.get(id);
  const { games, fields, startDate, endDate, isSchedulePublished } = tournament;

  const shownDivisions = new Set(
    filteredGames.map((game) => gameDivision(game, teamsById)),
  );
  const colorLegendByDivision = Object.fromEntries(
    Object.entries(colorLegend).filter(([key]) => shownDivisions.has(key)),
  );

  const sortedFields = useMemo(
    () => fields.toSorted((a, b) => a.sortIndex - b.sortIndex),
    [fields],
  );

  const dayNumber = useMemo(
    () => (date ? moment(date).diff(moment(startDate), 'days') + 1 : null),
    [date, startDate],
  );

  const availableFieldNames = useMemo(
    () => new Set(fields.map((field) => field.name)),
    [fields],
  );

  const fieldNameById = useMemo(() => {
    const map = new Map();
    fields.forEach((field) => {
      map.set(field.id, field.name);
    });
    return map;
  }, [fields]);

  const teamNameById = useMemo(() => {
    const map = new Map();
    teamsById.forEach((team) => {
      map.set(team.id, team.name);
    });
    return map;
  }, [teamsById]);

  const searchableDayGames = useMemo(() => {
    const toTeamId = (team) => (typeof team === 'string' ? team : team?.id);
    const toTeamName = (team) => {
      if (!team) return 'TBD';
      if (typeof team === 'string') return teamNameById.get(team) || 'TBD';
      return team.name || teamNameById.get(team.id) || 'TBD';
    };

    const getCoachLabel = (teamId) => {
      const names = (teamId ? coachesByTeamId.get(teamId) || [] : [])
        .map((c) => c.name)
        .filter(Boolean);
      return names.length ? names.join(', ') : 'No coaches';
    };

    return [...filteredGames]
      .map((game) => {
        const team1Id = toTeamId(game.team1);
        const team2Id = toTeamId(game.team2);

        const team1Name = toTeamName(game.team1);
        const team2Name = toTeamName(game.team2);

        const team1Label = `${team1Name} (${getCoachLabel(team1Id)})`;
        const team2Label = `${team2Name} (${getCoachLabel(team2Id)})`;

        const fieldName = fieldNameById.get(game.field) || game.field || 'TBD';
        const timeLabel = game.start ? time24To12H(game.start) : 'TBD';

        const refereeNames = (game.referees || [])
          .map((refId) => refereesById.get(refId)?.name)
          .filter(Boolean);

        // Keyed off the uids: a game with a deleted referee resolves to no
        // names, and labelling it "Not assigned" would make the search lie
        const refereesLabel = !(game.referees || []).length
          ? 'Not assigned'
          : refereeNames.join(', ') || '—';

        const [hours = 0, minutes = 0] = (game.start || '99:99')
          .split(':')
          .map(Number);
        const startMinutes = hours * 60 + minutes;

        return {
          id: game.id,
          fieldName,
          team1Label,
          team2Label,
          refereesLabel,
          timeLabel,
          startMinutes,
        };
      })
      .sort((a, b) => {
        if (a.startMinutes !== b.startMinutes) {
          return a.startMinutes - b.startMinutes;
        }

        const fieldCmp = a.fieldName.localeCompare(b.fieldName, 'en', {
          sensitivity: 'base',
        });
        if (fieldCmp !== 0) return fieldCmp;

        return a.id.localeCompare(b.id, 'en', { sensitivity: 'base' });
      });
  }, [
    filteredGames,
    teamNameById,
    coachesByTeamId,
    fieldNameById,
    refereesById,
  ]);

  const searchValues = useMemo(
    () =>
      searchableDayGames.map((game) => [
        `${game.timeLabel}, ${game.fieldName}`,
        game.team1Label,
        game.team2Label,
        `Referees: ${game.refereesLabel}`,
      ]),
    [searchableDayGames],
  );

  const highlightedGameId = useMemo(() => {
    if (highlightIndex === null || highlightIndex === undefined) return null;
    return searchableDayGames[highlightIndex]?.id || null;
  }, [highlightIndex, searchableDayGames]);

  const handleSearchMatch = useCallback((index) => {
    setHighlightIndex(index);
  }, []);

  const handleSearchTermClick = useCallback((value) => {
    const text = String(value ?? '').trim();
    if (!text) return;

    setSearchInputRequest({
      value: text,
      token: `${Date.now()}-${Math.random()}`,
    });
  }, []);

  const handleSearchTermHoverChange = useCallback((hoverKey) => {
    setActiveSearchHoverKey(hoverKey || null);
  }, []);

  useEffect(() => {
    if (fieldFilter === null) {
      localStorage.removeItem(storageField);
      return;
    }
    localStorage.setItem(storageField, fieldFilter);
  }, [fieldFilter]);

  useEffect(() => {
    if (fieldFilter === null && sortedFields.length > 0)
      setFieldFilter(sortedFields[0].name);
  }, [sortedFields, fieldFilter]);

  useEffect(() => {
    setFilteredGames(
      games.filter((game) => moment(game.date).isSame(date, 'day')),
    );
  }, [games, date]);

  useEffect(() => {
    setHighlightIndex(null);
  }, [date]);

  useEffect(() => {
    setActiveSearchHoverKey(null);
  }, [date]);

  useEffect(() => {
    if (highlightIndex === null || highlightIndex === undefined) return;
    if (!searchableDayGames[highlightIndex]) setHighlightIndex(null);
  }, [highlightIndex, searchableDayGames]);

  useEffect(() => {
    if (!highlightedGameId) return;

    const gameToShow = searchableDayGames[highlightIndex];
    if (!gameToShow) return;

    const mobileSingleFieldView =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches;

    if (
      mobileSingleFieldView &&
      gameToShow.fieldName &&
      availableFieldNames.has(gameToShow.fieldName) &&
      gameToShow.fieldName !== fieldFilter
    ) {
      setFieldFilter(gameToShow.fieldName);
      return;
    }

    const allBlocks = Array.from(document.querySelectorAll('[data-game-id]'));
    const matches = allBlocks.filter(
      (node) => node.getAttribute('data-game-id') === highlightedGameId,
    );

    const visibleMatch =
      matches.find((node) => {
        const rect = node.getBoundingClientRect();
        return node.offsetParent !== null && rect.width > 0 && rect.height > 0;
      }) || matches[0];

    if (!visibleMatch || typeof visibleMatch.scrollIntoView !== 'function')
      return;

    visibleMatch.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [
    highlightedGameId,
    searchableDayGames,
    highlightIndex,
    fieldFilter,
    availableFieldNames,
  ]);

  useEffect(() => {
    // On mount, try to restore previously selected date from localStorage
    const saved = localStorage.getItem(storageDate);
    if (saved) {
      setDate(moment(saved, 'YYYY-MM-DD'));
    } else {
      // Otherwise default to tournament startDate and persist it
      setDate(startDate);
      localStorage.setItem(storageDate, moment(startDate).format('YYYY-MM-DD'));
    }
  }, [startDate]);

  useEffect(() => {
    setPublished(isSchedulePublished);
  }, [isSchedulePublished]);

  useEffect(() => {
    const pendingGameId = searchParams.get('highlight');
    if (!pendingGameId || searchableDayGames.length === 0) return;

    searchParams.delete('highlight');
    setSearchParams(searchParams, { replace: true });

    const index = searchableDayGames.findIndex((g) => g.id === pendingGameId);
    if (index !== -1) setHighlightIndex(index);
  }, [searchableDayGames, searchParams, setSearchParams]);

  const setGameFilter = async (args) => {
    setFilteredGames((prevFilteredGames) =>
      prevFilteredGames.map((game) =>
        game.id === args.gameId ? { ...game, ...args, id: args.gameId } : game,
      ),
    );
    await tournamentGameUpdate(args);
  };

  const handleClear = () => {
    // Clears all games for the selected date
    const onClear = async () => {
      if (!tournament || !tournament.games) return;

      const gamesToDelete = tournament.games.filter((game) =>
        moment(game.date).isSame(date, 'day'),
      );

      setSpinner(true);
      await tournamentGamesDelete({
        id,
        gameIds: gamesToDelete.map((game) => game.id),
      }).catch((error) => console.error('Error deleting games:', error));
      setSpinner(false);
    };

    openModal({
      header: `Clear the day`,
      body: <p className='font-16'>Are you sure you want to clear the day?</p>,
      buttons: [
        {
          text: 'Cancel',
          className: 'bw',
        },
        {
          text: 'Clear',
          className: 'danger',
          onClick: onClear,
        },
      ],
    });
  };

  const handleGenerate = () =>
    openModal({
      header: 'Generate schedule',
      body: <ScheduleGenerator id={id} date={date} />,
    });

  const handleAddGame = () =>
    openModal({
      header: 'Add game',
      body: (
        <GameBlockEdit
          id={id}
          game={{ date }}
          setBracketsPopup={setBracketsPopup}
        />
      ),
    });

  const handleDataChange = (newDate) => {
    const momentDate = moment(newDate);
    setDate(momentDate);
    localStorage.setItem(storageDate, momentDate.format('YYYY-MM-DD'));
  };

  const handlePublish = async (val) => {
    setPublished(val);

    try {
      await tournamentUpdate({ id, isSchedulePublished: val });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExportJpeg = async () => {
    if (!date || dayNumber === null || spinner || !printRef.current) return;
    setSpinner(true);
    try {
      const blob = await captureJpegBlob(printRef.current);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${tournament.name} - ${date.format('YYYY-MM-DD')}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setSpinner(false);
    }
  };

  // Helper to render a list of Field components
  const renderFields = (fieldsList) =>
    fieldsList.map((field) => (
      <Field
        key={field.id}
        field={field.id}
        id={id}
        timeSlots={timeSlots}
        games={filteredGames}
        colorLegend={colorLegend}
        setBracketsPopup={setBracketsPopup}
        setGame={setGameFilter}
        highlightedGameId={highlightedGameId}
        onSearchTermClick={handleSearchTermClick}
        onSearchHoverChange={handleSearchTermHoverChange}
        activeSearchHoverKey={activeSearchHoverKey}
      />
    ));

  return (
    <GuidedTour
      steps={userIsFieldManager ? steps : undefined}
      tourKey='scheduler-v1'
    >
      <div className='scheduler'>
        <div className='control-wrap sticky-top'>
          <div className='controls'>
            <div className={'left-block' + (!userIsAdmin ? ' one-row' : '')}>
              <div className='first-row'>
                <InputDate
                  value={date?.toDate()}
                  onChange={handleDataChange}
                  className='date'
                  minDate={startDate.toDate()}
                  maxDate={endDate.toDate()}
                  placeholder='Day'
                  small
                />
                {userIsAdmin && <FieldsList id={id} fields={fields} />}
              </div>
              <div className='second-row'>
                <SearchInput
                  className='search'
                  values={searchValues}
                  onMatch={handleSearchMatch}
                  externalSearchRequest={searchInputRequest}
                  placeholder='Search'
                  small
                />
              </div>
            </div>
            {userIsAdmin && (
              <div className='btns-wrap'>
                <div className='first-row'>
                  <button
                    className='bw small fa-img icon'
                    onClick={handleExportJpeg}
                    disabled={spinner}
                  >
                    Save as JPEG
                  </button>
                  <button
                    className='small clear-btn danger'
                    onClick={handleClear}
                    disabled={spinner}
                  >
                    Clear
                  </button>
                  <button
                    className='bw small icon fa-gears'
                    onClick={handleGenerate}
                    disabled={spinner}
                  >
                    Generate
                  </button>
                  <button
                    className='bw small add-btn'
                    onClick={handleAddGame}
                    disabled={spinner}
                  >
                    Add&nbsp;game
                  </button>
                </div>
                <div className='second-row'>
                  <CheckBox
                    label='Published'
                    value={published}
                    onChange={handlePublish}
                    small
                    className='published-check-box'
                  />
                </div>
              </div>
            )}
          </div>
          <ColorLegend id={id} colorLegend={colorLegendByDivision} />
          <HorizontalScrollbar scrollRef={scheduleWrapRef} />
          <FieldHeadersRow fields={sortedFields} scrollRef={scheduleWrapRef} />
          <div className='field-filter-mobile-wrap'>
            <Select
              className='field-filter-mobile'
              options={fields.map((f) => f.name)}
              small
              value={fieldFilter}
              onChange={setFieldFilter}
            />
          </div>
        </div>
        <TimelineFields
          wrapRef={scheduleWrapRef}
          timeSlots={timeSlots}
          sortedFields={sortedFields}
          fieldFilter={fieldFilter}
          renderFields={renderFields}
          spinner={spinner}
        />
        <div
          className={'brackets-popup modal' + (bracketsPopup ? ' show' : '')}
        >
          <GetClickOutside
            active={!!bracketsPopup}
            onClick={() => setBracketsPopup(null)}
          >
            {bracketsPopup && (
              <div
                className='block'
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <Brackets
                  id={id}
                  fields={fields}
                  highlightRound={bracketsPopup?.highlightRound}
                  highlightPlacement={bracketsPopup?.highlightPlacement}
                  initDivision={bracketsPopup?.initDivision}
                  onPick={
                    bracketsPopup?.onPick
                      ? (slotData) => {
                          bracketsPopup.onPick(slotData);
                          setBracketsPopup(null);
                        }
                      : undefined
                  }
                />
                <button
                  className='close-btn fa-cross transparent'
                  onClick={() => setBracketsPopup(null)}
                  aria-label='Close'
                />
              </div>
            )}
          </GetClickOutside>
        </div>
        <div ref={printRef} className='scheduler-print-wrap'>
          {date && (
            <SchedulePrintView
              tournamentId={id}
              date={date}
              sortedFields={sortedFields}
              filteredGames={filteredGames}
              colorLegend={colorLegend}
              timeSlots={timeSlots}
            />
          )}
        </div>
      </div>
    </GuidedTour>
  );
}
